import { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import './ChatInput.css';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGES = 5;
const MAX_DIMENSION = 1568;
const JPEG_QUALITY = 0.85;
const MAX_GIF_BYTES = 3 * 1024 * 1024;

function readAsDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function loadImage(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = dataUrl;
    });
}

async function processFile(file) {
    const rawUrl = await readAsDataURL(file);
    // Preserve GIFs (canvas loses animation); reject if too big.
    if (file.type === 'image/gif') {
        if (file.size > MAX_GIF_BYTES) return null;
        return {
            data: rawUrl.split(',')[1],
            media_type: 'image/gif',
            name: file.name,
            preview: rawUrl,
        };
    }
    const img = await loadImage(rawUrl);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // JPEG has no alpha: without this, transparent regions of a PNG render black
    // and swallow any text sitting on them.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    return {
        data: dataUrl.split(',')[1],
        media_type: 'image/jpeg',
        name: file.name,
        preview: dataUrl,
    };
}

const ChatInput = forwardRef(function ChatInput({ onSend, disabled, placeholder = "Start typing..." }, ref) {
    const [value, setValue] = useState('');
    const [images, setImages] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);

    useImperativeHandle(ref, () => ({
        prefill(text) {
            setValue(text);
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(text.length, text.length);
                }
            }, 0);
        },
    }));

    const addFiles = useCallback(async (files) => {
        const valid = files.filter(f => ALLOWED_TYPES.includes(f.type));
        if (valid.length === 0) return;
        const toAdd = valid.slice(0, Math.max(0, MAX_IMAGES - images.length));
        if (toAdd.length === 0) return;
        const results = await Promise.all(toAdd.map(f => processFile(f).catch(() => null)));
        const converted = results.filter(Boolean);
        if (converted.length === 0) return;
        setImages(prev => [...prev, ...converted].slice(0, MAX_IMAGES));
    }, [images.length]);

    const autosize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if ((!value.trim() && images.length === 0) || disabled) return;
        const imgPayload = images.map(i => ({ data: i.data, media_type: i.media_type }));
        onSend(value, imgPayload.length > 0 ? imgPayload : null);
        setValue('');
        setImages([]);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        await addFiles(files);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = (idx) => {
        setImages(prev => prev.filter((_, i) => i !== idx));
    };

    const handlePaste = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItems = items.filter(i => i.type.startsWith('image/'));
        if (imageItems.length === 0) return;
        e.preventDefault();
        const files = imageItems.map(i => i.getAsFile()).filter(Boolean);
        await addFiles(files);
    };

    const hasImageDrag = (e) => {
        const types = e.dataTransfer?.types;
        if (!types) return false;
        return Array.from(types).includes('Files');
    };

    const handleDragEnter = (e) => {
        if (disabled || !hasImageDrag(e)) return;
        e.preventDefault();
        dragCounter.current += 1;
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        if (disabled || !hasImageDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (e) => {
        if (disabled) return;
        e.preventDefault();
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDrop = async (e) => {
        if (disabled) return;
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length === 0) return;
        await addFiles(files);
    };

    return (
        <form
            className={`input-box${isDragging ? ' dragging' : ''}`}
            onSubmit={handleSubmit}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className="input-drop-overlay" aria-hidden="true">
                    Drop images to attach
                </div>
            )}
            {images.length > 0 && (
                <div className="input-previews">
                    {images.map((img, i) => (
                        <div key={i} className="input-preview">
                            <img src={img.preview} alt="" />
                            <button type="button" className="input-preview-remove" onClick={() => removeImage(i)}>
                                &times;
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="input-row">
                <button
                    type="button"
                    className="attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || images.length >= MAX_IMAGES}
                    title="Attach image"
                >
                    <svg viewBox="0 0 24 24"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <textarea
                    ref={textareaRef}
                    className="chat-input"
                    placeholder={placeholder}
                    rows={1}
                    value={value}
                    onChange={(e) => { setValue(e.target.value); autosize(); }}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    autoComplete="off"
                />
                <button type="submit" className="send-btn" disabled={disabled || (!value.trim() && images.length === 0)}>
                    <svg viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </button>
            </div>
        </form>
    );
});

export default ChatInput;
